import { useMutation, useQuery } from "@tanstack/react-query";
import {
  MFFrequencyParam,
  MFOrderTypeParam,
  MFTransactionTypeParam,
} from "store/features/MFOrders";
import axiosInstance from "utils/axiosInstance";

/**
 * Client-side view of a single order item the client is being asked to consent
 * to. Mirrors the advisor-side `OrderBatchItem` but is a scoped subset returned
 * for one client.
 */
export interface ConsentItem {
  itemId: number;
  portfolioId: number;
  portfolioName?: string | null;
  folioNumber: string | null;
  amount: string | null;
  units: string | null;
  redemptionType?: string | null;
  switchType?: string | null;
  status?: string | null;
  schemeName?: string | null;
  switchInSchemeName?: string | null;
}

export interface ConsentOrder {
  orderLabel: string;
  orderType: MFOrderTypeParam;
  transactionType: MFTransactionTypeParam;
  frequency?: MFFrequencyParam | null;
  dayOfMonth?: number | null;
  installments?: number | null;
  isPerpetual?: boolean;
  startDate?: string | null;
  scheduledFor?: string | null;
  gcode: string;
  switchInGcode?: string | null;
  itemCount: number;
  totalAmount: string;
  portfolioLabel: string;
  buyAmount: string;
  sellAmount: string;
  items: ConsentItem[];
  text: string;
  html: string;
  noExpiry: boolean;
  schemeName: string;
}

export interface ConsentResponse {
  status: string;
  kind: string;
  clientName: string;
  order: ConsentOrder;
  items?: ConsentItem[];
  consentStatement: string;
  isTerminal: boolean;
}

export interface ConsentApiResponse {
  status: boolean;
  message: string;
  data: ConsentResponse;
}

export interface PlaceResultItem {
  id: number;
  portfolioId: number;
  folioNumber: string | null;
  amount: string | null;
  units: string | null;
  status: string;
  errorCode: string | null;
  errorMessage: string | null;
  paymentTokenUrl: string | null;
  paymentMethod: string | null;
  scheme?: { name?: string | null } | null;
}

export interface PlaceResult {
  status: string;
  summary?: { total: number; success: number; failed: number };
  paymentTokenUrl?: string | null;
  items: PlaceResultItem[];
}

export interface PlaceApiResponse {
  status: boolean;
  message: string;
  data: PlaceResult;
}

interface ConsentArgs {
  orderId: string | null;
  clientId: string | null;
  portfolioId: string | null;
  token: string;
}

/**
 * The consent flow has no `localStorage` token — the only credential is the
 * token issued by OTP verification, held in the form. `axiosInstance` bakes in
 * the localStorage token at creation, so every consent call overrides the
 * `Authorization` header per-request with the OTP token.
 */
const authHeader = (token: string) => ({
  headers: { Authorization: `Token ${token}` },
});

// Backend may return snake_case (per the API collection) or camelCase.
// Read either casing so the consent screen renders regardless.
const pick = <T>(obj: any, camel: string, snake: string): T =>
  obj?.[camel] ?? obj?.[snake];

const normalizeItem = (raw: any): ConsentItem => ({
  itemId: pick(raw, "itemId", "item_id"),
  portfolioId: pick(raw, "portfolioId", "portfolio_id"),
  portfolioName: pick(raw, "portfolioName", "portfolio_name"),
  folioNumber: pick(raw, "folioNumber", "folio_number"),
  amount: raw?.amount ?? null,
  units: raw?.units ?? null,
  redemptionType: pick(raw, "redemptionType", "redemption_type"),
  switchType: pick(raw, "switchType", "switch_type"),
  status: raw?.status ?? null,
  schemeName: pick(raw, "schemeName", "scheme_name"),
  switchInSchemeName: pick(raw, "switchInSchemeName", "switch_in_scheme_name"),
});

const normalizeConsent = (raw: any): ConsentResponse => {
  const orderRaw = raw?.order ?? {};
  const itemsRaw: any[] = orderRaw?.items ?? raw?.items ?? [];
  return {
    status: raw?.status,
    kind: raw?.kind,
    clientName: pick(raw, "clientName", "client_name"),
    order: {
      orderLabel: pick(orderRaw, "orderLabel", "order_label"),
      orderType: pick(orderRaw, "orderType", "order_type"),
      transactionType: pick(orderRaw, "transactionType", "transaction_type"),
      frequency: orderRaw?.frequency ?? null,
      dayOfMonth: pick(orderRaw, "dayOfMonth", "day_of_month"),
      installments: orderRaw?.installments ?? null,
      isPerpetual: pick(orderRaw, "isPerpetual", "is_perpetual"),
      startDate: pick(orderRaw, "startDate", "start_date"),
      scheduledFor: pick(orderRaw, "scheduledFor", "scheduled_for"),
      gcode: orderRaw?.gcode,
      switchInGcode: pick(orderRaw, "switchInGcode", "switch_in_gcode"),
      itemCount: pick(orderRaw, "itemCount", "item_count"),
      totalAmount: pick(orderRaw, "totalAmount", "total_amount"),
      portfolioLabel: pick(orderRaw, "portfolioLabel", "portfolio_label"),
      buyAmount: pick(orderRaw, "buyAmount", "buy_amount"),
      sellAmount: pick(orderRaw, "sellAmount", "sell_amount"),
      items: itemsRaw.map(normalizeItem),
      text: orderRaw?.text,
      html: orderRaw?.html,
      noExpiry: pick(orderRaw, "noExpiry", "no_expiry"),
      schemeName: pick(orderRaw, "schemeName", "scheme_name"),
    },
    items: itemsRaw.map(normalizeItem),
    consentStatement: pick(raw, "consentStatement", "consent_statement"),
    isTerminal: pick(raw, "isTerminal", "is_terminal"),
  };
};

export const useMFConsent = ({
  orderId,
  clientId,
  portfolioId,
  token,
}: ConsentArgs) => {
  const consentQuery = useQuery({
    queryKey: ["MF_CONSENT_ORDER", orderId, clientId, portfolioId],
    enabled: !!orderId && !!clientId && !!token && !!portfolioId,
    queryFn: async () => {
      const response = await axiosInstance.get<ConsentApiResponse>(
        `/api/orders/consent/${orderId}/`,
        { ...authHeader(token), params: { portfolio_id: portfolioId } },
      );
      return normalizeConsent(response.data.data);
    },
  });

  // Single endpoint: `consent: true` places the order, `consent: false`
  // records a decline. `consent_text` overrides the displayed statement
  // verbatim; null → backend stores the canonical statement.
  const placeMutation = useMutation({
    mutationKey: ["MF_CONSENT_PLACE", orderId],
    mutationFn: (args: {
      consent: boolean;
      consentText?: string | null;
      paymentType?: string | null;
    }) =>
      axiosInstance.post(
        `/api/orders/consent/${orderId}/place/`,
        {
          consent: args.consent,
          consent_text: args.consentText ?? null,
          ...(args.paymentType ? { paymentMethod: args.paymentType } : {}),
        },
        authHeader(token),
      ),
  });

  return { consentQuery, placeMutation };
};
